import React, { useEffect, useRef, useState, useCallback } from 'react'
import PropTypes from 'prop-types'
import gsap from 'gsap'
import { Draggable } from 'gsap/all'
import { InertiaPlugin } from '@utils/gsap/InertiaPlugin'
import {
  PartnersCard,
  PartnersWrapper,
  PartnersCardThumbnail,
  PartnersCarousel,
  PartnersHeader,
  PartnersFooter,
  PartnersMain,
  PartnersNav,
  PartnersNavItem,
  PartnersTitle,
  PartnersCardWrapper,
  PartnersCardLink
} from './index.style'
import Container from '@components/Container'
import Spacer from '@components/Spacer'
import Button from '@components/Button'
import { GatsbyImage } from 'gatsby-plugin-image'
import IconArrowRight from '@svgs/IconArrowRight'
import PageTransitionLink from '@components/PageTransitionLink'
import useGetColor from '../useGetColor'
import { Heading2Serif, TextBody } from '@components/TextStyles'

const Partners = ({ data, parentService , backgroundColor: bgColor, textColor: tColor }) => {
  const {partnerItems: items, title, subTitle, background, titleStyle, useDarkLogos, isClickable} = data
  const [backgroundColor, textColor] = useGetColor(background, bgColor, tColor)
  const [activeIndex, setActiveIndex] = useState(0)
  const [carouselAtEnd, setCarouselAtEnd] = useState(false)
  const activeIndexRef = useRef(activeIndex)
  const $partnersWrapper = useRef()
  const $partners = useRef()
  const $singleCard = useRef()
  const slideWidth = useRef()

  const getSlideWidth = useCallback(() => {
    const cardWidth = parseFloat(
      window.getComputedStyle($singleCard.current).width.replace(/px$/, '')
    )
    const cardMargin = parseFloat(
      window.getComputedStyle($singleCard.current).marginLeft.replace(/px$/, '')
    )
    return cardWidth + cardMargin
  }, [])

  const getMaxWidth = () =>
    -$partners.current.offsetWidth + $partnersWrapper.current.offsetWidth

  const getCarouselX = useCallback(targetX => {
    const maxWidth = getMaxWidth()
    const isEnd = targetX < maxWidth
    const isStart = targetX > 0
    const x = isEnd ? maxWidth : isStart ? 0 : targetX

    setCarouselAtEnd(isEnd)
    return x
  }, [])

  const goToCard = useCallback(
    index => {
      gsap.to($partners.current, {
        x: getCarouselX(-(slideWidth.current * index)),
        ease: 'inOut',
        duration: 0.3,
        onComplete: () => {
          setActiveIndex(index)
        },
      })
    },
    [getCarouselX]
  )

  useEffect(() => {
    gsap.registerPlugin(Draggable, InertiaPlugin)

    Draggable.create($partners.current, {
      type: 'x',
      edgeResistance: 1,
      inertia: true,
      snap: function (endValue) {
        const maxWidth = getMaxWidth()
        const clampedValue = gsap.utils.clamp(maxWidth, 0, endValue)

        setActiveIndex(Math.ceil(Math.abs(clampedValue / slideWidth.current)))
        setCarouselAtEnd(clampedValue <= maxWidth)
        return clampedValue
      },
    })

    const applyDraggableBounds = () => {
      slideWidth.current = getSlideWidth()

      Draggable.get($partners.current).applyBounds({
        minX: $partnersWrapper.current.offsetWidth - $partners.current.offsetWidth,
        maxX: 0,
      })

      goToCard(activeIndexRef.current)
    }

    applyDraggableBounds()

    window.addEventListener('resize', applyDraggableBounds)

    return () => {
      window.removeEventListener('resize', applyDraggableBounds)
    }
  }, [getSlideWidth, goToCard])

  useEffect(() => {
    activeIndexRef.current = activeIndex
  }, [activeIndex])

  const getSlidePaginationAmount = () => {
    if (window.innerWidth > 2060) {
      return 5
    } else if (window.innerWidth > 1520) {
      return 5
    } else if (window.innerWidth > 990) {
      return 4
    } else if (window.innerWidth > 590) {
      return 3
    } else {
      return 2
    }
  }

  const goToPrevious = () => {
    goToCard(Math.max(activeIndex - getSlidePaginationAmount(), 0))
  }

  const goToNext = () => {
    goToCard(
      Math.min(activeIndex + getSlidePaginationAmount(), items.length - 1)
    )
  }
  const getItemImage = (item) => {
      if(useDarkLogos) {
        if(item.darkImage) {
          return <GatsbyImage
              image={item.darkImage.gatsbyImageData}
              alt={item.darkImage.description}
              width={240}
              height={240}
          />
        }else {
          return <GatsbyImage
              image={item.featuredImage.gatsbyImageData}
              alt={item.featuredImage.description}
              width={240}
              height={240}
          />
        }
      }else {
        if(item.lightImage) {
          return <GatsbyImage
              image={item.lightImage.gatsbyImageData}
              alt={item.lightImage.description}
              width={240}
              height={240}
          />
        }else {
          return <GatsbyImage
              image={item.featuredImage.gatsbyImageData}
              alt={item.featuredImage.description}
              width={240}
              height={240}
          />
        }
      }
  } 

  return (
    <PartnersMain color={textColor} background={backgroundColor}>
      <Container>
        <PartnersWrapper ref={$partnersWrapper}>
          <Spacer size={[30, 95]} />

          <PartnersHeader>
            <div>
              {(titleStyle === "Signifier Light") ?
                <Heading2Serif maxWidth={11}>{title}</Heading2Serif>
                :
                <PartnersTitle>{title}</PartnersTitle>
              }
              <Spacer size={12} />
              <TextBody maxWidth={22}>{subTitle}</TextBody>
            </div>
            {(titleStyle !== "Signifier Light") &&
              <PartnersNav backgroundColor={backgroundColor} fontColor={textColor}>
                <PartnersNavItem>
                  <Button
                    variant="outline"
                    onClick={goToPrevious}
                    size="large"
                    color="white"
                    disabled={activeIndex === 0}
                  >
                    PREVIOUS
                  </Button>
                </PartnersNavItem>
                <PartnersNavItem>
                  <Button
                    variant="outline"
                    onClick={goToNext}
                    size="large"
                    color="white"
                    disabled={carouselAtEnd}
                  >
                    NEXT
                  </Button>
                </PartnersNavItem>
              </PartnersNav>
            }
          </PartnersHeader>

          <PartnersCarousel ref={$partners} centered={false}>
            {React.Children.toArray(
              items.map((item, itemIndex) => (
                <PartnersCardWrapper
                  ref={itemIndex === 1 ? $singleCard : undefined}
                >
                  {isClickable ?
                  <PageTransitionLink to={`what-we-do/${parentService}/${item.slug}`} target="_blank">
                    <PartnersCard hasBackground isDark={(background === 'White') ? false : true}>
                      
                      {getItemImage(item) && 
                        <PartnersCardThumbnail>
                          {getItemImage(item)}
                        </PartnersCardThumbnail>
                      }
                    </PartnersCard>
                    <PartnersCardLink><span className="more">Learn more</span><IconArrowRight /></PartnersCardLink>
                  </PageTransitionLink>
                  :
                  <PartnersCard isDark={(background === 'White') ? false : true}>
                        
                    {getItemImage(item) && 
                      <PartnersCardThumbnail>
                        {getItemImage(item)}
                      </PartnersCardThumbnail>
                    }
                  </PartnersCard>
                  }
                </PartnersCardWrapper>
              ))
            )}
          </PartnersCarousel>

          {(titleStyle === "Signifier Light") &&
            <PartnersFooter>
              <PartnersNav backgroundColor={backgroundColor} fontColor={textColor}>
                <PartnersNavItem>
                  <Button
                    variant="outline"
                    onClick={goToPrevious}
                    size="large"
                    color="white"
                    disabled={activeIndex === 0}
                  >
                    PREVIOUS
                  </Button>
                </PartnersNavItem>
                <PartnersNavItem>
                  <Button
                    variant="outline"
                    onClick={goToNext}
                    size="large"
                    color="white"
                    disabled={carouselAtEnd}
                  >
                    NEXT
                  </Button>
                </PartnersNavItem>
              </PartnersNav>
            </PartnersFooter>
          }

          <Spacer size={[60, 100]} />
        </PartnersWrapper>
      </Container>
    </PartnersMain>
  )
}

Partners.propTypes = {
  data: PropTypes.object,
  bgColor: PropTypes.string,
  tColor: PropTypes.string,
  parentService: PropTypes.string
}

export default Partners
